import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'

import { App } from './App'
import { RouteScrollReset } from './layout/RouteScrollReset'
import { ApiError } from './lib/api/errors'
import './styles/tokens.css'
import './styles/base.css'
import './styles/motion.css'
import './styles/workspace.css'
// Last: it refines the surfaces the files above lay out.
import './styles/material.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      // Never retry a client error — the request is wrong, not unlucky.
      retry: (count, error) =>
        error instanceof ApiError && error.status >= 400 && error.status < 500
          ? false
          : count < 3,
      retryDelay: (i) => Math.min(1000 * 2 ** i, 30_000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: { retry: false },
  },
})

try {
  const theme = localStorage.getItem('convo:theme')
  if (theme === 'light' || theme === 'dark') document.documentElement.dataset['theme'] = theme
} catch { /* Use the system theme when storage is unavailable. */ }

const root = document.getElementById('root')
if (!root) throw new Error('#root not found')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RouteScrollReset />
        {/* Top-level boundary: lazy routes outside the AppShell (onboarding, the
            workspace picker) need one too. */}
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
